import { useEffect, useState } from 'react';

// 主题：默认深色；用户可以选深色、浅色或跟随系统（存在 localStorage 的 wd-theme：light / dark / system）。
// index.html 里有一段同样的判断，先于样式执行，避免打开页面时闪一下。

export type Theme = 'dark' | 'light';
export type ThemePref = Theme | 'system';

const EVENT = 'wd-theme-change';
const systemTheme = (): Theme => (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

export function currentTheme(): Theme {
  return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
}

export function themePref(): ThemePref {
  try {
    const t = localStorage.getItem('wd-theme');
    return t === 'dark' || t === 'light' || t === 'system' ? t : 'dark';
  } catch {
    return 'dark';
  }
}

function apply(t: Theme) {
  document.documentElement.dataset.theme = t;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', t === 'light' ? '#F7F8F8' : '#08090A');
  window.dispatchEvent(new Event(EVENT));
}

export function setThemePref(p: ThemePref) {
  try {
    localStorage.setItem('wd-theme', p);
  } catch {
    /* 隐私模式下忽略 */
  }
  apply(p === 'system' ? systemTheme() : p);
}

/** 兼容旧调用：直接设成深色或浅色 */
export const setTheme = (t: Theme) => setThemePref(t);

/** 当前主题（深 / 浅），切换时组件跟着刷新；选了「跟随系统」时系统切换也会跟着变 */
export function useTheme(): [Theme, () => void] {
  const [theme, set] = useState<Theme>(currentTheme);
  useEffect(() => {
    const sync = () => set(currentTheme());
    const mq = matchMedia('(prefers-color-scheme: dark)');
    const onSystem = () => themePref() === 'system' && apply(systemTheme());
    window.addEventListener(EVENT, sync);
    mq.addEventListener('change', onSystem);
    return () => {
      window.removeEventListener(EVENT, sync);
      mq.removeEventListener('change', onSystem);
    };
  }, []);
  return [theme, () => setThemePref(currentTheme() === 'dark' ? 'light' : 'dark')];
}
